import { LEGACY_LICENSE_MAPPING } from '../constants/license';

export interface LicenseInfo {
  viewDatabase: boolean;
  ccsModule: boolean;
  viewAnalogue: boolean;
  datasets?: string[];
  modules?: string[];
}

/**
 * Check if user has access to a specific dataset
 */
export const hasDatasetAccess = (licenseInfo: LicenseInfo, datasetId: string): boolean => {
  // Check new modular system first
  if (licenseInfo.datasets && licenseInfo.datasets.length > 0) {
    return licenseInfo.datasets.includes(datasetId);
  }
  
  // Fallback to legacy system for backward compatibility
  if (datasetId === 'ccs_projects') {
    return licenseInfo.ccsModule;
  }
  
  if (['subsurface_with_grid', 'subsurface_no_grid', 'infrastructure', 'blocks', 'licences'].includes(datasetId)) {
    return licenseInfo.viewDatabase;
  }
  
  return false;
};

/**
 * Check if user has access to a specific module
 */
export const hasModuleAccess = (licenseInfo: LicenseInfo, moduleId: string): boolean => {
  // Check new modular system first
  if (licenseInfo.modules && licenseInfo.modules.length > 0) {
    return licenseInfo.modules.includes(moduleId);
  }
  
  // Fallback to legacy system for backward compatibility
  if (moduleId === 'ccs_module') {
    return licenseInfo.ccsModule;
  }
  
  if (moduleId === 'analogue_finder') {
    return licenseInfo.viewAnalogue;
  }
  
  return false;
};

/**
 * Check if user has access to any of the specified datasets
 */
export const hasAnyDatasetAccess = (licenseInfo: LicenseInfo, datasetIds: string[]): boolean => {
  return datasetIds.some(datasetId => hasDatasetAccess(licenseInfo, datasetId));
};

/**
 * Check if user has access to any of the specified modules
 */
export const hasAnyModuleAccess = (licenseInfo: LicenseInfo, moduleIds: string[]): boolean => {
  return moduleIds.some(moduleId => hasModuleAccess(licenseInfo, moduleId));
};

/**
 * Get all accessible dataset IDs for the user
 */
export const getAccessibleDatasets = (licenseInfo: LicenseInfo): string[] => {
  // If new modular system is in use
  if (licenseInfo.datasets && licenseInfo.datasets.length > 0) {
    return licenseInfo.datasets;
  }
  
  // Fallback to legacy system
  const accessibleDatasets: string[] = [];
  
  if (licenseInfo.viewDatabase) {
    accessibleDatasets.push('subsurface_with_grid', 'subsurface_no_grid', 'infrastructure', 'blocks', 'licences');
  }
  
  if (licenseInfo.ccsModule) {
    accessibleDatasets.push('ccs_projects');
  }
  
  return accessibleDatasets;
};

/**
 * Get all accessible module IDs for the user
 */
export const getAccessibleModules = (licenseInfo: LicenseInfo): string[] => {
  // If new modular system is in use
  if (licenseInfo.modules && licenseInfo.modules.length > 0) {
    return licenseInfo.modules;
  }
  
  // Fallback to legacy system
  const accessibleModules: string[] = [];
  
  if (licenseInfo.ccsModule) {
    accessibleModules.push('ccs_module');
  }
  
  if (licenseInfo.viewAnalogue) {
    accessibleModules.push('analogue_finder');
  }
  
  return accessibleModules;
};

/**
 * Convert legacy license info to new modular format
 */
export const convertLegacyToModular = (licenseInfo: LicenseInfo): LicenseInfo => {
  if (licenseInfo.datasets && licenseInfo.modules) {
    return licenseInfo; // Already in modular format
  }
  
  const datasets: string[] = [];
  const modules: string[] = [];
  
  if (licenseInfo.viewDatabase) {
    datasets.push(...LEGACY_LICENSE_MAPPING.viewDatabase);
  }
  
  if (licenseInfo.ccsModule) {
    modules.push(...LEGACY_LICENSE_MAPPING.ccsModule);
  }
  
  if (licenseInfo.viewAnalogue) {
    modules.push(...LEGACY_LICENSE_MAPPING.viewAnalogue);
  }
  
  return {
    ...licenseInfo,
    datasets,
    modules
  };
};

/**
 * Check if user should see subsurface data with or without grids
 */
export const hasSubsurfaceAccess = (licenseInfo: LicenseInfo, includeGrids = true): boolean => {
  if (includeGrids) {
    return hasDatasetAccess(licenseInfo, 'subsurface_with_grid');
  } else {
    return hasDatasetAccess(licenseInfo, 'subsurface_no_grid') || hasDatasetAccess(licenseInfo, 'subsurface_with_grid');
  }
}; 